import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileText, Mail, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getPolicies, getPolicy } from "../api";
import useSettings from "../hooks/useSettings";

const Policy = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const settings = useSettings();
  const [policy, setPolicy] = useState(null);
  const [list, setList] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getPolicies().then(setList).catch(() => setList([]));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPolicy(null);
    setNotFound(false);
    getPolicy(slug)
      .then((p) => {
        setPolicy(p);
        document.title = `${p.title} · PhotonPE`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-[160px] pb-32 text-center">
          <h1 className="text-3xl font-extrabold text-[#0D1B2A]">Policy not found</h1>
          <button onClick={() => navigate("/")} className="mt-6 text-[#0EA5E9] font-semibold">← Back to Home</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero */}
      <section className="pt-[120px] pb-14" style={{ background: "linear-gradient(135deg,#050C17,#0D1B2A)" }}>
        <div className="pp-container">
          <span className="inline-flex items-center gap-2 text-[#10B981] font-semibold uppercase tracking-wider text-sm">
            <FileText size={16} /> Policies
          </span>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold text-white">{policy ? policy.title : "Loading…"}</h1>
          {policy?.updated_label && <p className="mt-3 text-white/60 text-sm">{policy.updated_label}</p>}
        </div>
      </section>

      <section className="py-14">
        <div className="pp-container grid lg:grid-cols-[240px_1fr] gap-10">
          {/* Sidebar nav */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-1.5">
              <div className="text-xs font-bold uppercase tracking-wide text-[#64748B] mb-3">All Policies</div>
              {list.map((l) => (
                <button
                  key={l.slug}
                  onClick={() => navigate(`/policies/${l.slug}`)}
                  className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    l.slug === slug ? "bg-[#0D1B2A] text-white" : "text-[#1E293B] hover:bg-[#F0FDFA]"
                  }`}
                >
                  {l.title}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="max-w-3xl">
            {policy ? (
              <div className="pp-article" dangerouslySetInnerHTML={{ __html: policy.body || "" }} />
            ) : (
              <div className="text-[#475569]">Loading policy…</div>
            )}

            {/* Contact box */}
            <div className="mt-12 rounded-3xl p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" style={{ background: "linear-gradient(135deg,#F0FDFA,#f6f9fd)" }}>
              <div>
                <h3 className="text-lg font-bold text-[#0D1B2A]">Have questions about this policy?</h3>
                <p className="text-sm text-[#475569] mt-1">Our team is happy to help you understand your rights.</p>
              </div>
              <a
                href={`mailto:${settings.email}`}
                className="group inline-flex items-center gap-2 text-white font-semibold px-6 py-3 rounded-full whitespace-nowrap"
                style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}
              >
                <Mail size={16} /> Contact Us
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Policy;
